import Backoffice from "@snark/backoffice"
import projectors from "@/assets/projectors.json";
import Vue from "vue";

const context = {
	account: Backoffice.populator("account", {}),
	building: Backoffice.populator("building", {}),
	vlbox: Backoffice.populator("vlbox", {}),
};

function getBrandOptions() {
	return Object.keys(projectors).map(b => ({ value: b, label: b }));
}

function getModelOptions(brand) {
	return (projectors[brand] || []).map(m => ({ value: m.model, label: m.model }));
}

async function getAccountOptions() {
	const accounts = await Vue.backoffice.requestor.get("/account");
	return (Array.isArray(accounts) ? accounts : [])
		.map(account => ({
			value: account._id,
			label: account.name || account._id
		}))
		.sort((a, b) => a.label.localeCompare(b.label));
}

async function getBuildingOptions(input) {
	const accountId = typeof input === "string" ? input : input?.object?.accountId;
	if (!accountId) return [];

	const buildings = await Vue.backoffice.requestor.get("/building", null, { accountId });
	return (Array.isArray(buildings) ? buildings : [])
		.filter(building => building.accountId?.toString?.() === accountId.toString())
		.map(building => ({
			value: building._id,
			label: building.name || building._id
		}))
		.sort((a, b) => a.label.localeCompare(b.label));
}

function applySpecsToItem(item) {
	const specs = updateSpecsFromModel({ brand: item.brand, model: item.model });
	Object.assign(item, specs);
}

function removeBackendOnlyFields(item) {
	const cleanItem = { ...item };
	delete cleanItem.account;
	delete cleanItem.building;
	delete cleanItem.vlbox;
	delete cleanItem.projectorStatus;
	delete cleanItem.lastSeenAt;
	delete cleanItem.statusGraceMs;
	delete cleanItem.createdAt;
	delete cleanItem.updatedAt;
	return cleanItem;
}

function updateSpecsFromModel(form) {
	if (!form || !form.brand || !form.model) return {};

	const models = projectors[form.brand] || [];
	const selected = models.find(m => m.model === form.model);

	if (!selected) return {};

	return {
		serie: selected.serie || "",
		lumens: selected.lumens || 0,
		resolutionHorizontal: selected.resolutionHorizontal || 0,
		resolutionVertical: selected.resolutionVertical || 0,
		power: selected.power || 0,
		powerEco: selected.powerEco || 0,
	};
}

export default {
	name: "projector",
	collection: "projector",
	idProperty: "_id",
	section: "devices",
	backPopulateList: ["account", "building", "vlbox"],
	filterList: function(items) {
		return Array.isArray(items) ? items.filter(item => !item.type || item.type === "projector") : items;
	},
	labels: { singleTitle: "Projector", listTitle: "Projectors" },
	emptyItem: (() => {
    const defaultBrand = Object.keys(projectors)[0];
    const defaultModel = getModelOptions(defaultBrand)[0]?.value;
    return {
        accountId: null,
        buildingId: null,
        type: "projector",
        brand: defaultBrand,
        model: defaultModel,
    };
})(),
	fields: [
		{ name: "_id", label: "Id", readOnly: true, hideOnCreate: true },
		{
			name: "type",
			label: "Type",
			formatter: Backoffice.formatters.mapping({ "projector": "Projector" }),
			editor: {
				type: "select",
				options: [
					{ value: "projector", label: "Projector" }
				]
			}
		},
		{ name: "name", label: "Name" },
		{
			name: "accountId",
			label: "Account Id",
			formatter: Backoffice.formatters.linkObjectId("account", { idProperty: "_id", label: context.account.formatter() }),
			editor: {
				type: "select",
				options: getAccountOptions,
				transform: (value, form) => {
					if (form.accountId !== value) {
						form.buildingId = null;
						form.vlboxId = null;
					}
					return value;
				},
			},
		},
		{
			name: "buildingId",
			label: "Building Id",
			formatter: Backoffice.formatters.linkObjectId("building", { idProperty: "_id", label: context.building.formatter() }),
			editor: {
				type: "select",
				options: getBuildingOptions,
				watch: "accountId",
				transform: (value, form) => {
					if (form.buildingId !== value) {
						form.vlboxId = null;
					}
					return value;
				},
			},
		},
		{ name: "addressIP", label: "Address IP" },
		{ name: "lastSeenAt", label: "Last Seen At", readOnly: true, hideOnCreate: true, formatter: Backoffice.formatters.date("DD/MM/YYYY HH:mm:ss") },
		{ name: "statusGraceMs", label: "Status Grace (ms)", readOnly: true, hideOnCreate: true },
		{ name: "adminInterface", label: "Administration Interface" },
		{
			name: "brand",
			label: "Brand",
			editor: {
				type: "select",
				options: () => getBrandOptions(),
			},
		},
		{
			name: "model",
			label: "Model",
			editor: {
				type: "select",
				options: (input) => {
					const brand = typeof input === "string"
						? input
						: input?.object?.brand;

					return brand ? getModelOptions(brand) : [];
				},
				watch: "brand",
			}
		},
		{ name: "lumens", label: "Lumens", editor: { type: "float", computeValue: (form) => updateSpecsFromModel(form).lumens, watch: ['model', 'brand'] } },
		//{ name: "serie", label: "Série", editor: { type: "text", computeValue: (form) => updateSpecsFromModel(form).serie, watch: ['model', 'brand'] } },
		{ name: "resolutionHorizontal", label: "Résolution horizontale", editor: { type: "float", computeValue: (form) => updateSpecsFromModel(form).resolutionHorizontal, watch: ['model', 'brand'] } },
		{ name: "resolutionVertical", label: "Résolution verticale", editor: { type: "float", computeValue: (form) => updateSpecsFromModel(form).resolutionVertical, watch: ['model', 'brand'] } },
		{ name: "power", label: "Puissance (W)", editor: { type: "float", computeValue: (form) => updateSpecsFromModel(form).power, watch: ['model', 'brand'] } },
		{ name: "powerEco", label: "Puissance Eco (W)", editor: { type: "float", computeValue: (form) => updateSpecsFromModel(form).powerEco, watch: ['model', 'brand'] } },
		{
			name: "picture",
			label: "Picture",
			formatter: Backoffice.formatters.subProperty('path', Backoffice.formatters.picture({
				urlPrefix: process.env.VUE_APP_API_STATIC_URL + "/", maxHeight: 300
			})),
			editor: {
				type: "image",
				accept: "image/png,image/jpeg",
				preview: {
					urlPrefix: process.env.VUE_APP_API_STATIC_URL + "/",
					contain: true,
					formatUrl: Backoffice.formatters.subProperty('path'),
				}
			},
			noChildren: true,
		},
		{
			name: "state",
			label: "State",
			readOnly: true,
			hideOnCreate: true,
			formatter: Backoffice.formatters.mapping({ "deleted": "Deleted", "draft": "Draft" }),
		},
		{
			name: "createdAt",
			label: "Created At",
			readOnly: true,
			hideOnCreate: true,
			formatter: Backoffice.formatters.date("DD/MM/YYYY"),
		},
		{
			name: "updatedAt",
			label: "Updated At",
			readOnly: true,
			hideOnCreate: true,
			formatter: Backoffice.formatters.date("DD/MM/YYYY"),
		},
		{
			name: "vlboxId",
			label: "Vlbox Id",
			formatter: Backoffice.formatters.linkObjectId("vlbox", { idProperty: "_id", label: context.vlbox.formatter() }),
			editor: context.vlbox.editor(),
		},
		{
			name: "disabled",
			label: "Disabled Status",
			editor: { type: "boolean" }
		},
		{
			name: "orientation",
			label: "Orientation",
			formatter: Backoffice.formatters.mapping({
				"horizontal": "Horizontale",
				"vertical-cw": "Verticale (rotation horaire)",
				"vertical-ccw": "Verticale (rotation anti-horaire)"
			}),
			editor: {
				type: "select",
				options: [
					{ value: "horizontal", label: "Horizontale" },
					{ value: "vertical-cw", label: "Verticale (rotation horaire)" },
					{ value: "vertical-ccw", label: "Verticale (rotation anti-horaire)" }
				]
			}
		},
		{
			name: "projectorStatus",
			label: "Projector Status",
			readOnly: true,
			hideOnCreate: true,
			formatter: (value) => (value ? JSON.stringify(value) : "")
		},
	],
	listColumns: [
		{ name: "name", label: "Name" },
		{ name: "account", label: "Account", formatter: Backoffice.formatters.subProperty('name'), nosingle: true },
		{ name: "building", label: "Building", formatter: Backoffice.formatters.subProperty('name'), nosingle: true },
		{ name: "addressIP", label: "Address IP" },
		{ name: "adminInterface", label: "administration Interface" },
		{ name: "brand", label: "Brand" },
		{ name: "model", label: "Model" },
		{ name: "vlbox", label: "Vlbox", formatter: Backoffice.formatters.subProperty('name'), nosingle: true },
		{
			name: "orientation",
			label: "Orientation",
			formatter: Backoffice.formatters.mapping({
				"horizontal": "Horizontale",
				"vertical-cw": "Verticale (rot. horaire)",
				"vertical-ccw": "Verticale (rot. anti-horaire)"
			})
		},
	],
	loadSingle() {
		return Promise.all([
			context.account.load(),
			context.building.load(),
			context.vlbox.load(),
		]);
	},
	completeSingle(item) {
		if (item.brand && item.model) {
       		applySpecsToItem(item);
    	}
		return Promise.resolve(item);
	},
	beforeSave(item) {
		return Promise.resolve(removeBackendOnlyFields(item));
	}
};
